import { DataRole } from '@mode-switch/express';
import { DomainResult, QueryResult } from '@mode-switch/query';
import { ColorTarget, EncField, EncFilterField, Encoding, VariableType, ViewFieldTypes } from '@mode-vegas/vegas';
import { ChartDataTypes, ChartTypes, FlamingoTypes, HashKeyTypes, OrgTypes } from '@mode/shared/contract-common';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { CompiledField } from '@mode/shared/util-switch';

export interface ColorsModalData {
  palettes: readonly OrgTypes.ColorPalette[];
  currentPaletteToken: string;
  formatDefaultColor: string | undefined; // This is the fallback palette color from the Format object
  queryRunToken: string | undefined;
  vizToken: string;
  view: FlamingoTypes.ModeView;
  encoding: Encoding | undefined;
  colorField: EncField;
  renderModel: RenderDataModel | undefined;
  fieldColors: ChartTypes.FieldColorMap | undefined;
  fieldFormats: ChartTypes.FieldFormatMap | undefined;
  chartHasSplitLegend: boolean;
  isManualUpdateMode: boolean;
  savedColorTarget: ColorTarget;
  isChildModal?: boolean;
  hideRangeControls?: boolean;
  hideSteps?: boolean;
  hideStepLabels?: boolean;

  applyColors: (colorField: EncField, fieldColor: ChartTypes.FieldColor, colorTarget: ColorTarget) => void;
  onCancel: () => void;
}

export abstract class ColorsModalFacade {
  abstract open(data: ColorsModalData): void;
  abstract openFromFieldList(
    primaryField: FlamingoTypes.ModeAttr,
    view: FlamingoTypes.ModeView,
    palettes: readonly OrgTypes.ColorPalette[],
    queryRunToken: string,
    applyColors: (colorField: EncField, fieldColor: ChartTypes.FieldColor, colorTarget: ColorTarget) => void,
    onCancel: () => void
  ): void;
}

export interface RenderDataModel extends SchemaModel {
  getSchema(): ChartDataTypes.Column[];
  getSeriesData(series: string): ReadonlyArray<QueryResult.Item>;
  getTotalSeriesData(series: string): Readonly<TotalSeriesData>;
  getSubtotalSeriesData(encoding: Encoding, series: string): Readonly<TotalSeriesData>;
  getSeriesSchema(series: string): ChartDataTypes.Column[];
  isEmpty(): boolean;

  getColorDomain(encoding: Encoding, format: ChartTypes.ChartFormat, seriesId?: string, applyFormat?: boolean): any[];

  getNestedSortDomain(encoding: Encoding, alias: string): NestedSortDomain | undefined;
  getSortDomain<R extends DataRole>(alias: string): DomainResult<R> | null;
  getSortDomain<R extends DataRole>(): SortDomainData<R>;
}

export interface SchemaModel {
  makeViewField(field: FlamingoTypes.ModeField): ViewFieldTypes | undefined;
  makeField(encField: EncField): FlamingoTypes.ModeField | undefined;
  buildField(formula: string, seriesVarType: VariableType, id: string | undefined): FlamingoTypes.ModeField | undefined;
  buildViewNamesField(name: string): FlamingoTypes.ModeField;
  makeColumn(field: ViewFieldTypes | FlamingoTypes.ModeField, name: string): ChartDataTypes.Column;
  compileEncField(encField: EncField): CompiledField;
  compileField(fieldOrAttr: FlamingoTypes.ModeField): CompiledField;
  validateFormula(source: string): [boolean, string | null]; // TODO: Make a proper validation result type
  validateFilter(encFilter: EncFilterField, allowAggregate?: boolean): [boolean, string | null]; // TODO: Make a proper validation result type
}

export interface TotalSeriesData {
  readonly series: string;
  readonly col?: readonly QueryResult.Item[];
  readonly row?: readonly QueryResult.Item[];
  readonly all?: readonly QueryResult.Item[];
}

export interface NestedSortDomain {
  readonly alias: string;
  readonly outer: string[];
  readonly keyFn: HashKeyTypes.HashFn;
  readonly domain: ReadonlyMap<number, ReadonlyMap<unknown, number>>;
}

export interface SortDomainData<R extends DataRole> {
  readonly [alias: string]: DomainResult<R> | null;
}
